import {Attributes, DataStatus} from 'platform/components';
import {Heading, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {useAnnualMileageQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {parseDate, RequiredTestIdProps, suffixTestId, Nullish} from 'shared/web';

import {VehicleServiceIntervalFluidAttributes} from './VehicleServiceIntervalFluidAttributes';
import {VehicleServiceIntervalReplacementAttributes} from './VehicleServiceIntervalReplacementAttributes';

interface VehicleServiceIntervalsAttributesProps extends RequiredTestIdProps {
  vehicleData: GetVehicleApiResponseV2 | Nullish;
}

export function VehicleServiceIntervalsAttributes(props: VehicleServiceIntervalsAttributesProps) {
  const formatDateTime = useDateTimeFormatter();

  const annualMileageQuery = useAnnualMileageQuery(
    {vehicleId: props.vehicleData?.id || ''},
    {skip: !props.vehicleData?.id}
  );

  const serviceInspection = props.vehicleData?.serviceIntervals?.serviceInspection;
  const engineOil = props.vehicleData?.serviceIntervals?.engineOil;
  const gearboxOil = props.vehicleData?.serviceIntervals?.gearboxOil;
  const fourByFourOilDistributor = props.vehicleData?.serviceIntervals?.fourByFourOilDistributor;
  const timingBelt = props.vehicleData?.serviceIntervals?.timingBelt;
  const brakeFluid = props.vehicleData?.serviceIntervals?.brakeFluid;
  const coolantFluid = props.vehicleData?.serviceIntervals?.coolantFluid;

  return (
    <VStack spacing={4}>
      <Heading size={4}>{i18n.t('entity.vehicle.labels.serviceInspection')}</Heading>
      <Attributes
        rows={[
          {
            label: i18n.t('entity.vehicle.labels.dateOfLastInspection'),
            value: serviceInspection?.lastServiceInspectionDate
              ? formatDateTime('dateShort', parseDate(serviceInspection?.lastServiceInspectionDate))
              : null,
          },
          {
            label: i18n.t('entity.vehicle.labels.replacementAfterMonths'),
            value: serviceInspection?.replacementAfterMonths,
          },
          {
            label: i18n.t('entity.vehicle.labels.dateOfNextServiceInspection'),
            value: serviceInspection?.nextServiceInspectionDate
              ? formatDateTime('dateShort', parseDate(serviceInspection?.nextServiceInspectionDate))
              : null,
          },
          {
            label: `${i18n.t('entity.vehicle.labels.lastServiceInspection')} (${i18n.t('general.metric.km')})`,
            value: serviceInspection?.lastServiceInspectionMileage,
          },
          {
            label: `${i18n.t('entity.vehicle.labels.replacementAfterMileage')} (${i18n.t('general.metric.km')})`,
            value: serviceInspection?.replacementAfterMileage,
          },
          {
            label: `${i18n.t('entity.vehicle.labels.nextServiceInspection')} (${i18n.t('general.metric.km')})`,
            value: serviceInspection?.nextServiceInspectionMileage,
          },
        ]}
        data-testid={suffixTestId('serviceInspection', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.engineOil')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={engineOil}
        data-testid={suffixTestId('engineOil', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.gearboxOil')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={gearboxOil}
        data-testid={suffixTestId('gerboxOil', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.fourByFourOilDistributor')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={fourByFourOilDistributor}
        data-testid={suffixTestId('fourByFourOilDistributor', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.timingBelt')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={timingBelt}
        data-testid={suffixTestId('timingBelt', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.brakeFluid')}</Heading>
      <VehicleServiceIntervalFluidAttributes
        part={brakeFluid}
        data-testid={suffixTestId('brakeFluid', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.coolantFluid')}</Heading>
      <VehicleServiceIntervalFluidAttributes
        part={coolantFluid}
        data-testid={suffixTestId('coolantFluid', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.annualMileage')}</Heading>
      <DataStatus isLoading={annualMileageQuery.isLoading} isError={annualMileageQuery.isError}>
        <Attributes
          rows={[
            {
              label: `${i18n.t('entity.vehicle.labels.serviceVisits')} (${i18n.t('general.metric.km')})`,
              value: annualMileageQuery.data?.annualMileage,
            },
          ]}
          data-testid={suffixTestId('annualMileage', props)}
        />
      </DataStatus>
    </VStack>
  );
}
